import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import { Icons, Typography } from '@usepepper/sprout-kit-ui';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { NAME_MAX_LENGTH } from '@/experiences/sales-portal/domain/order-guides/blocs/utils/naming-utils';
import {
  NativeSyntheticEvent,
  Pressable,
  TextInput,
  TextInputKeyPressEventData,
  View,
} from 'react-native';
import { useEffect, useRef, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';

const EXPANDED_WIDTH = 240;
const CHIP_HEIGHT = 32;
// Round up the measured width; there are some cases (eg. first render on 90% zoom)
// where we are short a fraction of a pixel which leads to the button wrapping lines.
// Pad the width by a pixel so the label always fits on one line.
const COLLAPSED_WIDTH_FIT_BUFFER = 1;
const ACTION_BUTTON_SIZE = 22;
const ICON_SIZE = 14;
const WIDTH_TIMING = { ...DEFAULT_EASING_IN_OUT_CONFIG, duration: 150 };
const TRIGGER_FADE_TIMING = { ...DEFAULT_EASING_IN_OUT_CONFIG, duration: 110 };

interface OrderGuideNewGroupChipProps {
  onCreateGroup: (name: string) => void;
  onEditingChange?: (isEditing: boolean) => void;
  validateName: (name: string) => boolean;
}

/**
 * Pill trigger at the end of the group-selector row that morphs into an
 * inline name editor. The pill's width animates between the trigger's
 * measured width and a fixed editor width while the trigger fades out; the
 * clipped container reveals the editor as it expands.
 */
export const OrderGuideNewGroupChip: React.FC<OrderGuideNewGroupChipProps> = ({
  onCreateGroup,
  onEditingChange,
  validateName,
}) => {
  const [styles, theme] = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [collapsedWidth, setCollapsedWidth] = useState<number | null>(null);
  const inputRef = useRef<TextInput>(null);
  const triggerRef = useRef<View>(null);
  const isScreenFocused = useIsFocused();

  // Open/close notify the parent (onEditingChange) so it can track a
  // create-in-progress; go through these rather than setIsEditing directly.
  const openEditor = () => {
    setIsEditing(true);
    onEditingChange?.(true);
  };

  const closeEditor = () => {
    setIsEditing(false);
    setName('');
    onEditingChange?.(false);
  };

  // Sections stay mounted when hidden — drop any in-progress draft on blur so
  // a stale editor doesn't greet the user when they come back.
  if (!isScreenFocused && isEditing) {
    closeEditor();
  }

  // Defer focus by one frame so the editor has mounted; preventScroll keeps
  // focus from scrolling the chip row sideways.
  useEffect(() => {
    if (!isEditing) {
      return;
    }
    const id = requestAnimationFrame(() => {
      const node = inputRef.current as
        | (TextInput & { focus: (opts?: { preventScroll?: boolean }) => void })
        | null;
      node?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [isEditing]);

  const containerAnimatedStyle = useAnimatedStyle(() => {
    if (collapsedWidth === null) {
      return {};
    }
    return {
      width: withTiming(
        isEditing ? EXPANDED_WIDTH : collapsedWidth,
        WIDTH_TIMING,
      ),
    };
  }, [isEditing, collapsedWidth]);

  const triggerAnimatedStyle = useAnimatedStyle(
    () => ({ opacity: withTiming(isEditing ? 0 : 1, TRIGGER_FADE_TIMING) }),
    [isEditing],
  );

  const trimmedName = name.trim();
  const canConfirm = validateName(trimmedName);

  const handleChangeName = (value: string) => {
    setName(value);
  };

  const handleConfirm = () => {
    if (!validateName(trimmedName)) {
      return;
    }
    onCreateGroup(trimmedName);
    closeEditor();
  };

  // Escape closes the editor. The input holds focus while open, so its own key
  // handler is required — a global listener wouldn't see the bubbled keydown.
  const handleKeyPress = (
    event: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => {
    if (event.nativeEvent.key === Key.Escape) {
      closeEditor();
    }
  };

  return (
    <Animated.View
      onLayout={(event) => {
        if (collapsedWidth === null) {
          setCollapsedWidth(
            event.nativeEvent.layout.width + COLLAPSED_WIDTH_FIT_BUFFER,
          );
        }
      }}
      style={[
        styles.container,
        isEditing ? styles.containerEditing : null,
        containerAnimatedStyle,
      ]}
    >
      <Animated.View style={triggerAnimatedStyle}>
        <Pressable
          accessibilityElementsHidden={isEditing}
          accessibilityLabel={i18n.t(
            'sales_portal.order_guides_list.groups.new_group',
          )}
          accessibilityRole="button"
          disabled={isEditing}
          importantForAccessibility={isEditing ? 'no-hide-descendants' : 'auto'}
          onPress={openEditor}
          ref={triggerRef}
          style={styles.trigger}
        >
          <Icons.Plus color="$iconPrimary" size={ICON_SIZE} />
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={1}
            size="$sm"
            variant="label"
          >
            {i18n.t('sales_portal.order_guides_list.groups.new_group')}
          </Typography>
        </Pressable>
      </Animated.View>
      {isEditing ? (
        <View style={styles.editor}>
          <TextInput
            accessibilityLabel={i18n.t(
              'sales_portal.order_guides_list.groups.new_group_placeholder',
            )}
            maxLength={NAME_MAX_LENGTH}
            onChangeText={handleChangeName}
            onKeyPress={handleKeyPress}
            onSubmitEditing={handleConfirm}
            placeholder={i18n.t(
              'sales_portal.order_guides_list.groups.new_group_placeholder',
            )}
            placeholderTextColor={theme.sk.color.text.tertiary}
            ref={inputRef}
            style={[styles.input, { color: theme.sk.color.text.primary }]}
            value={name}
          />
          <Pressable
            accessibilityLabel={i18n.t(
              'sales_portal.order_guides_list.groups.new_group_confirm',
            )}
            accessibilityRole="button"
            accessibilityState={{ disabled: !canConfirm }}
            disabled={!canConfirm}
            onPress={handleConfirm}
            style={[
              styles.actionButton,
              canConfirm ? styles.confirmButton : styles.confirmButtonDisabled,
            ]}
          >
            <Icons.Check
              color={
                canConfirm
                  ? theme.sk.color.icon.ondark
                  : theme.sk.color.icon.disabled
              }
              size={ICON_SIZE}
            />
          </Pressable>
          <Pressable
            accessibilityLabel={i18n.t(
              'sales_portal.order_guides_list.groups.new_group_cancel',
            )}
            accessibilityRole="button"
            onPress={closeEditor}
            style={[styles.actionButton, styles.cancelButton]}
          >
            <Icons.X color={theme.sk.color.icon.primary} size={ICON_SIZE} />
          </Pressable>
        </View>
      ) : null}
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  actionButton: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusRound,
    height: ACTION_BUTTON_SIZE,
    justifyContent: 'center',
    width: ACTION_BUTTON_SIZE,
  },
  cancelButton: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  confirmButton: {
    backgroundColor: theme.sk.color.bg.button.primary.default,
  },
  confirmButtonDisabled: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  container: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusRound,
    borderWidth: 1,
    height: CHIP_HEIGHT,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  containerEditing: {
    borderColor: theme.sk.color.border.input.active,
  },
  editor: {
    alignItems: 'center',
    bottom: 0,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    left: 0,
    paddingLeft: theme.sk.spacing.spaceSMedium,
    paddingRight: theme.sk.spacing.spaceXsmall,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  input: {
    flex: 1,
    fontFamily: theme.sk.typography.paragraph.sm.fontFamily,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    minWidth: 0,
    padding: 0,
  },
  trigger: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
  },
}));
